import { Breakpoints, useBreakpoint, useTranslation } from '@nextgen-web-framework/all';
import { ModularBlockComponent } from '@commerce-webcomponents/ui-composite';
import { FC, useRef, useState } from 'react';
import { DescType, ImageComponent, ImageType, ShortDescription } from '../../Molecules';
import styles from './ProductDetails.module.scss';
import { ProductDetailsPropsTypes } from './types/propsTypes';

const ProductDetails: FC<ProductDetailsPropsTypes> = ({
  productDetail,
  cmsData,
}: ProductDetailsPropsTypes): JSX.Element => {
  const [showReadMore, setShowReadMore] = useState<boolean>(false);

  const ref = useRef<HTMLDivElement>(null);
  const descriptionContainerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLImageElement>(null);
  const t = useTranslation();
  const viewportType = useBreakpoint();
  const isDesktop = [Breakpoints.LG, Breakpoints.XL].includes(viewportType as Breakpoints);

  const toggleShowContent = () => {
    setShowReadMore((prev) => {
      if (descriptionContainerRef.current && isDesktop) {
        descriptionContainerRef.current.style.height = !prev
          ? `${(imageContainerRef.current?.children?.[0]?.clientHeight || 0) + 60}px`
          : 'auto';
      }
      return !prev;
    });
  };

  const readMoreLabel = `${t('product.productDetails.read')} ${t('product.productIngredient.viewMore')}`;
  const readLessLabel = `${t('product.productDetails.read')} ${t('product.productIngredient.viewLess')}`;

  // Description should be longer than the image height
  let showReadSection =
    (imageContainerRef.current?.children?.[0]?.clientHeight || 0) <
    (descriptionContainerRef.current?.clientHeight || 0);

  if (viewportType === Breakpoints.MD) {
    showReadSection = (ref.current?.clientHeight || 0) > 134;
  } else if ([Breakpoints.SM, Breakpoints.XS].includes(viewportType as Breakpoints)) {
    showReadSection = (ref.current?.clientHeight || 0) > 180;
  }

  return (
    <>
      {!!productDetail && Object.keys(productDetail)?.length > 0 && (
        <div className={styles['cpw-all-details-container']} data-testid="details-container-id">
          <div className={styles['details-header']} data-testid="details-header-id">
            <h3 className={styles['remove-extra-margin']}>{t('product.productDetails.header')}</h3>
          </div>
          <div className={styles['details-container']}>
            {productDetail.image && (
              <div className={styles['details-image']} data-testid="details-image-id" ref={imageContainerRef}>
                <ImageComponent
                  alt={productDetail.image?.main?.altText || ''}
                  link={productDetail.image?.main?.publicUrl as string}
                  imagetype={ImageType.PRODUCT_DETAILS}
                  className={styles['product-details-image']}
                />
              </div>
            )}
            <div
              className={`${styles['title-description-container']}`}
              ref={descriptionContainerRef}
              data-testid="title-description-container"
            >
              <div className={styles['details-title']} data-testid="details-title-id">
                <h2 className={`${styles['remove-extra-margin']} ${styles['details-title-header']}`}>
                  {productDetail.title}
                </h2>
              </div>
              <div
                className={
                  showReadMore
                    ? `${styles['details-description']} ${styles['details-description-more']}`
                    : `${styles['details-description']} ${styles['details-description-less']}`
                }
                data-testid="details-desc-id"
              >
                <ShortDescription
                  shortDescription={productDetail.text as string}
                  descType={DescType.DESCPRODUCTDETAILS}
                  descriptionRef={ref}
                />
              </div>
            </div>
          </div>
          {showReadSection && (
            <div className={`${styles['show-content']} ${showReadMore ? styles['show-content-more'] : ''}`}>
              {showReadMore ? (
                <span
                  data-testid="details-more-id"
                  role="button"
                  onClick={toggleShowContent}
                  tabIndex={0}
                  onKeyDown={toggleShowContent}
                  className={styles['product-details-expander-control']}
                  aria-label={readMoreLabel}
                >
                  {readMoreLabel}
                </span>
              ) : (
                <span
                  data-testid="details-less-id"
                  role="button"
                  aria-label={readLessLabel}
                  onClick={toggleShowContent}
                  tabIndex={0}
                  onKeyDown={toggleShowContent}
                  className={styles['product-details-expander-control']}
                >
                  {readLessLabel}
                </span>
              )}
            </div>
          )}
          {cmsData && cmsData.length > 0 && (
            <div className={styles['product-details-content-stack-container']}>
              <div className={styles['product-details-cms-data']}>
                <ModularBlockComponent data={cmsData} />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ProductDetails;
export type { ProductDetailsPropsTypes };
